/**
 * AI Impact Pipeline (AI Advisory P2)
 *
 * Turns the P1 news feed into GRADED, EVIDENCE-BACKED impacts on held instruments:
 *   news item → held ticker(s) → link type → direction/magnitude → confidence
 *
 * Guardrails are CODE, not prompt trust (blueprint §3.1):
 *   - evidence_quote REQUIRED and must literally appear in the source headline,
 *     otherwise the impact is dropped (anti-hallucination).
 *   - tickers must be actually held; unknown tickers are stripped, empty → drop.
 *   - link_type must be one of direct | peer_sector | macro_fx | commodity_input,
 *     anything else → drop ("no link" is dropped, not invented).
 *   - direction defaults to "unclear", magnitude is qualitative only (high/med/low),
 *     NEVER a predicted % — real $ exposure is computed from positions in code.
 *   - confidence clamped 0..1; surfaced = confidence >= CONFIDENCE_GATE (0.6).
 *
 * Storage: S.aiCopilot.impacts — REPLACED each analysis, capped at 25, lean fields
 * only (aiCopilot is a scalar sync field; no raw news is ever persisted).
 *
 * Exports:
 *   analyzeNewsImpact(newsItems)  — full pipeline, returns { ok, impacts, stats }
 *   getImpacts(surfacedOnly)      — read stored impacts
 *   exposureUSD(ticker)           — live $ exposure for a ticker (holdings + open trades)
 *   _validateImpacts, _buildImpactMessages — exported for unit tests
 */

import { S, save } from '../core/state.js';
import { chatJSON, isAIConfigured } from '../core/ai.js';
import { hVal, hTotUSD, hWealthUSD } from '../core/math.js';
import { toUSD, isLatinReadable, promptSafe } from '../core/utils.js';
import { getHeldInstruments } from './news-ingest.js';

export const CONFIDENCE_GATE = 0.6;
const MAX_IMPACTS = 25;
const MAX_NEWS_TO_LLM = 20; // keeps the mapping call fast — long TR headlines are token-heavy
const LINK_TYPES = ['direct', 'peer_sector', 'macro_fx', 'commodity_input'];
const DIRECTIONS = ['bullish', 'bearish', 'mixed', 'unclear'];
const MAGNITUDES = ['high', 'med', 'low'];

/** Live USD exposure for a ticker: priced holdings + open trade capital (leverage-adjusted). */
export function exposureUSD(ticker) {
  let usd = 0;
  (S.holdings || []).forEach(h => {
    if (h.ticker !== ticker) return;
    usd += hWealthUSD(h).usd;
  });
  (S.trades || []).forEach(t => {
    if (t.ticker !== ticker || t.status === 'Closed' || t.type === 'TimeDeposit') return;
    usd += toUSD((t.entry || 0) * (t.qty || 0) * (t.leverage || 1), t.currency || 'USD');
  });
  return Math.round(usd);
}

const IMPACT_SYSTEM_PROMPT = `You are the impact-mapping stage of a portfolio advisory pipeline.
You receive (1) HELD instruments with class and USD exposure, (2) recent news headlines.
Map each headline to the held instruments it plausibly affects.

STRICT RULES:
1. Only reference held tickers. If a headline affects nothing held, OMIT it entirely.
2. link_type: "direct" (names the company/asset), "peer_sector" (same sector/peer), "macro_fx" (rates, inflation, FX, geopolitics affecting the position's market/currency), "commodity_input" (commodity price feeding the business or the held commodity itself).
3. direction: bullish | bearish | mixed | unclear. When grounding is weak, use "unclear" — it is a valid answer.
4. magnitude: high | med | low. QUALITATIVE ONLY. Never estimate a price % move.
5. confidence: 0.0-1.0. Be conservative: direct company disclosure ~0.7-0.9, sector read-across ~0.4-0.6, macro links ~0.3-0.6.
6. evidence_quote: EXACT substring copied verbatim from the headline (the language of the headline, do not translate). No quote = the impact will be discarded.
7. evidence_quote_en: faithful English translation of evidence_quote (identical text when the source is already English). UI displays English only.
8. rationale: ONE sentence in English linking the quote to the position. No advice, no price targets.
9. Output strict JSON only:
{"impacts":[{"news_id":"<id>","tickers":["TICKER"],"link_type":"direct","direction":"bearish","magnitude":"med","confidence":0.7,"evidence_quote":"...","evidence_quote_en":"...","rationale":"..."}]}
Empty result is valid: {"impacts":[]}`;

/**
 * Build chatJSON messages. Exported for tests.
 * @param {Array} newsItems — normalized P1 items {id, ts, source, title, tickers, scope}
 * @param {Array} positions — [{ticker, cls, exposure}]
 */
export function _buildImpactMessages(newsItems, positions) {
  // promptSafe on every field that reaches the pipe- and line-structured block below. A
  // HEADLINE IS TEXT FROM THE INTERNET: a newline in one forges a row the feed never carried,
  // and `===` opens a section this builder did not write. `_validateImpacts` already grounds
  // the evidence quote in a real title, which is why the achievable harm was a flipped
  // direction and a fabricated rationale rather than a fabricated headline — but the frame
  // itself should not be forgeable. (2026-08-14)
  const posLines = positions.map(p => `${promptSafe(p.ticker, 16)} | ${promptSafe(p.cls, 24)} | $${p.exposure.toLocaleString('en-US')}`);
  const newsLines = newsItems.map(n => {
    const age = n.ts ? Math.round((Date.now() - n.ts) / 3600000) + 'h' : '?';
    return `id=${promptSafe(n.id, 40)} | ${promptSafe(n.source, 32)} | ${age} | ${promptSafe(n.title, 200)}`;
  });
  return [
    { role: 'system', content: IMPACT_SYSTEM_PROMPT },
    {
      role: 'user',
      content: '=== HELD INSTRUMENTS (ticker | class | USD exposure) ===\n' + posLines.join('\n')
        + '\n\n=== HEADLINES (id | source | age | title) ===\n' + newsLines.join('\n'),
    },
  ];
}

/** Whitespace-insensitive, case-insensitive containment check for evidence quotes. */
function _quoteInTitle(quote, title) {
  const norm = s => String(s).toLowerCase().replace(/\s+/g, ' ').trim();
  const q = norm(quote);
  return q.length >= 3 && norm(title).indexOf(q) !== -1;
}

/**
 * Validate + normalize raw LLM impacts. Pure; exported for tests.
 * @param {Array} raw — parsed impacts array from the LLM
 * @param {Map} newsById — id → news item
 * @param {Set} heldSet — held ticker symbols
 * @returns {{kept: Array, stats: Object}}
 */
export function _validateImpacts(raw, newsById, heldSet) {
  const stats = { received: 0, kept: 0, no_news: 0, no_ticker: 0, bad_link: 0, no_quote: 0, quote_mismatch: 0, bad_conf: 0 };
  const kept = [];
  if (!Array.isArray(raw)) return { kept, stats };
  stats.received = raw.length;

  raw.forEach(r => {
    if (!r || typeof r !== 'object') return;
    const news = newsById.get(r.news_id);
    if (!news) { stats.no_news++; return; }

    const tickers = (Array.isArray(r.tickers) ? r.tickers : [])
      .map(t => String(t).toUpperCase().trim())
      .filter(t => heldSet.has(t));
    if (!tickers.length) { stats.no_ticker++; return; }

    if (!LINK_TYPES.includes(r.link_type)) { stats.bad_link++; return; }

    const quote = typeof r.evidence_quote === 'string' ? r.evidence_quote.trim() : '';
    if (!quote) { stats.no_quote++; return; }
    if (!_quoteInTitle(quote, news.title)) { stats.quote_mismatch++; return; }

    const conf = Number(r.confidence);
    if (!isFinite(conf)) { stats.bad_conf++; return; }
    const confidence = Math.max(0, Math.min(1, conf));

    kept.push({
      newsId: news.id,
      newsTitle: String(news.title).slice(0, 200),
      newsUrl: news.url,
      source: news.source,
      newsTs: news.ts || null,
      tickers,
      linkType: r.link_type,
      direction: DIRECTIONS.includes(r.direction) ? r.direction : 'unclear',
      magnitude: MAGNITUDES.includes(r.magnitude) ? r.magnitude : 'low',
      confidence: +confidence.toFixed(2),
      evidenceQuote: quote.slice(0, 200),
      // English display copy (validation stays on the verbatim original above)
      evidenceQuoteEn: (typeof r.evidence_quote_en === 'string' && isLatinReadable(r.evidence_quote_en)
        ? r.evidence_quote_en.trim() : quote).slice(0, 220),
      rationale: typeof r.rationale === 'string' ? r.rationale.slice(0, 240) : '',
      surfaced: confidence >= CONFIDENCE_GATE,
    });
  });

  stats.kept = kept.length;
  return { kept, stats };
}

/**
 * Full pipeline: news items → LLM mapping → validation → capped store.
 * @param {Array} newsItems — normalized items from fetchAdvisoryNews().items
 * @returns {Promise<{ok:boolean, impacts:Array, stats?:Object, error?:string}>}
 */
export async function analyzeNewsImpact(newsItems) {
  if (!isAIConfigured()) return { ok: false, impacts: [], error: 'AI not configured' };
  const items = (newsItems || []).filter(n => n && n.id && n.title);
  if (!items.length) return { ok: true, impacts: [], stats: { received: 0, kept: 0 } };

  const held = getHeldInstruments();
  const heldSet = new Set([...held.bist, ...held.us, ...held.crypto, ...held.funds]);
  if (!heldSet.size) return { ok: true, impacts: [], stats: { received: 0, kept: 0 } };

  // Ticker-scoped items first (direct candidates), then macro; newest first within groups
  const ranked = [...items].sort((a, b) =>
    ((b.tickers?.length ? 1 : 0) - (a.tickers?.length ? 1 : 0)) || ((b.ts || 0) - (a.ts || 0))
  ).slice(0, MAX_NEWS_TO_LLM);

  const positions = [...heldSet].map(t => {
    const cls = held.bist.includes(t) ? 'BIST' : held.crypto.includes(t) ? 'Crypto' : held.funds.includes(t) ? 'TR Fund' : 'US';
    return { ticker: t, cls, exposure: exposureUSD(t) };
  }).filter(p => p.exposure > 0);

  let data;
  try {
    // 120s budget: the default 30s timeout was killing this call (the scan's own
    // LLM call runs ~90s+ with its own signal) — root cause of stale TR impacts.
    // 6000-token budget: 20 headlines x up to 25 impacts, each ~200 tokens of JSON
    // (two quote strings + rationale). The previous 2500 sat right on the cliff edge —
    // the last successful run kept 11 impacts, and the 12th truncated the response.
    const res = await chatJSON(_buildImpactMessages(ranked, positions), { maxTokens: 6000, temperature: 0, timeoutMs: 120000 });
    data = res.data;
  } catch (e) {
    console.warn('[IMPACT] LLM mapping failed:', e.message);
    return { ok: false, impacts: [], error: e.message };
  }

  const newsById = new Map(ranked.map(n => [n.id, n]));
  const { kept, stats } = _validateImpacts(data && data.impacts, newsById, heldSet);

  // English display is a hard UI rule — the mapping model sometimes omits or
  // copies evidence_quote_en, so guarantee it with a dedicated translation pass.
  await _ensureEnglishQuotes(kept);

  // Attach real $ exposure (code, never the LLM) + stamps
  const ts = new Date().toISOString();
  const impacts = kept
    .map(im => ({
      ...im,
      exposureUSD: im.tickers.reduce((a, t) => a + exposureUSD(t), 0),
      createdAt: ts,
    }))
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, MAX_IMPACTS);

  // Replace (never merge/grow) — capped, lean, no raw news persisted
  if (!S.aiCopilot) S.aiCopilot = { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] };
  S.aiCopilot.impacts = impacts;
  S.aiCopilot.lastImpact = { ts, analyzed: ranked.length, ...stats };
  save();

  if (stats.received > stats.kept) {
    console.warn('[IMPACT] Dropped', stats.received - stats.kept, 'of', stats.received, '—', JSON.stringify(stats));
  }
  try { window.__spektrxImpacts = { ts, impacts, stats }; } catch (_e) { /* non-browser */ }

  return { ok: true, impacts, stats };
}

/** True when a quote still needs an English display copy. Exported for tests. */
export function _needsTranslation(im) {
  const en = (im.evidenceQuoteEn || '').trim();
  if (!en) return true;
  // A display copy in a script the owner cannot read is not a display copy, whatever it says.
  // This is the case that shipped: an English source, a Mandarin "translation", and a trigger
  // that only looked for "unchanged and non-ASCII" — so it passed.
  if (!isLatinReadable(en)) return true;
  // Same text as the original AND carries non-ASCII LETTERS → not translated.
  // Typographic punctuation is stripped first: an English headline with an em dash or curly
  // quotes is not a foreign-language quote, and treating it as one sent nearly every English
  // headline through a paid translation call on every scan.
  const letters = en.replace(/[\u2010-\u2015\u2018-\u201F\u2026\u00A0\u2032\u2033]/g, '');
  return en === im.evidenceQuote && /[^\x00-\x7F]/.test(letters);
}

/**
 * Fill evidenceQuoteEn via one batched translation call for any impact whose
 * English copy is missing. Failure degrades honestly: originals stay, a warn
 * is logged — but this should be rare, not the norm.
 */
async function _ensureEnglishQuotes(impacts) {
  const todo = impacts.filter(_needsTranslation);
  if (!todo.length) return;
  try {
    const res = await chatJSON([
      { role: 'system', content: 'Translate each quote to natural English. The output MUST be English in the Latin alphabet — never Chinese, Cyrillic, Arabic or any other script, and if a quote is already English return it unchanged. Keep numbers/tickers as-is. Output strict JSON: {"translations":["..."]} — same order, same count.' },
      { role: 'user', content: JSON.stringify({ quotes: todo.map(t => t.evidenceQuote) }) },
    ], { maxTokens: 1200, temperature: 0, timeoutMs: 60000 });
    const tr = res.data && res.data.translations;
    if (Array.isArray(tr)) {
      todo.forEach((im, i) => {
        // Reject a "translation" that is not readable either — keep the original rather than
        // replace one unreadable string with another.
        if (typeof tr[i] === 'string' && isLatinReadable(tr[i])) im.evidenceQuoteEn = tr[i].trim().slice(0, 220);
      });
    }
  } catch (e) {
    console.warn('[IMPACT] Quote translation pass failed — originals shown:', e.message);
  }
}

/** Stored impacts (latest analysis). surfacedOnly limits to confidence >= 0.6. */
export function getImpacts(surfacedOnly = true) {
  const all = (S.aiCopilot && S.aiCopilot.impacts) || [];
  return surfacedOnly ? all.filter(i => i.surfaced) : all;
}
